import React, { useEffect } from 'react';
import styles from './PlaceholderPage.module.css';

/**
 * Polished full-page “coming soon” shell for dashboard routes under active development.
 *
 * @param {object} props
 * @param {string} props.title — Page title shown in the section header
 * @param {string} [props.subtitle] — Line under the title in the section header
 * @param {string} [props.heroEyebrow] — Small uppercase label above hero title
 * @param {string} props.description — Lead paragraph in the hero card
 * @param {string} props.icon — Emoji or short string shown in the hero
 * @param {{ title: string; text: string }[]} [props.highlights] — Optional feature bullets (grid)
 * @param {string} [props.badgeText] — Pill next to hero title (default: Coming soon)
 * @param {import('react').ReactNode} [props.leading] — Content rendered above the hero
 */
const PlaceholderPage = ({
  title,
  subtitle,
  heroEyebrow,
  description,
  icon,
  highlights = [],
  badgeText = 'Coming soon',
  leading = null,
  className,
}) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={[styles.placeholderPage, className].filter(Boolean).join(' ')}>
      <main className={styles.main}>
        <div className={styles.mainInner}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionHeading}>
              <h2 id="placeholder-section-title" className={styles.sectionTitle}>{title}</h2>
              {subtitle ? <p className={styles.sectionSubtitle}>{subtitle}</p> : null}
            </div>
          </div>
          {leading}
          <section className={styles.hero} aria-labelledby="placeholder-section-title">
            <div className={styles.heroGlow} aria-hidden />
            <div className={styles.heroTop}>
              <div className={styles.iconWrap}>
                <span className={styles.icon} aria-hidden>
                  {icon}
                </span>
              </div>
              <div className={styles.heroHeadingRow}>
                {heroEyebrow ? <p className={styles.heroEyebrow}>{heroEyebrow}</p> : <span />}
                {badgeText ? <span className={styles.badge}>{badgeText}</span> : null}
              </div>
            </div>
            <p className={styles.lead}>{description}</p>
            {highlights.length > 0 ? (
              <ul className={styles.highlights}>
                {highlights.map((item) => (
                  <li key={item.title} className={styles.highlight}>
                    <h3 className={styles.highlightTitle}>{item.title}</h3>
                    <p className={styles.highlightText}>{item.text}</p>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
          <p className={styles.footerNote}>We are finishing this experience. Check back soon.</p>
        </div>
      </main>
    </div>
  );
};

export default PlaceholderPage;
